/* eslint-disable no-underscore-dangle */
import { TYPES } from '@/entities/fields'
import GridEntity from '@/entities/grid'

const DATAGRID_CONFIG = {
    hasInputFilter: false,
    filterable: false,
    sideBar: false,
    editable: false,
    cleanFiltersButton: false,
    fitResponsive: true,
    defaultColDef: {
        checkboxSelection: GridEntity.isFirstCol,
        headerCheckboxSelection: GridEntity.isFirstCol,
    },
}

const ISSUE_FIELD = 'issuesCount'
const ISSUE_TYPES = {
    GLOBAL: 'global',
    ROW: 'row',
    CELL: 'cell',
}
const ISSUE_LEVELS = {
    ERROR: 'error',
    WARNING: 'warning',
}

const rowRuleIssueWarning = ({ data }) => !!Object.values(data._issueFields || {}).find(level => level === ISSUE_LEVELS.WARNING)

const rowRuleIssueError = ({ data }) => !!Object.values(data._issueFields || {}).find(level => level === ISSUE_LEVELS.ERROR)

const cellRuleIssueWarning = ({ colDef, data }) => {
    const { field } = colDef
    return (data._issueFields || {})[field] === ISSUE_LEVELS.WARNING
}

const cellRuleIssueError = ({ colDef, data }) => {
    const { field } = colDef
    return (data._issueFields || {})[field] === ISSUE_LEVELS.ERROR
}

const EXTRA_COLUMNS = [
    {
        headerName: 'Issues',
        field: ISSUE_FIELD,
        type: TYPES.INT,
        width: 100,
        pinned: 'left',
        suppressSizeToFit: true,
        cellClass: ['ag-cell-issues'],
        cellClassRules: {
            'ag-cell-issue-warning': rowRuleIssueWarning,
            'ag-cell-issue-error': rowRuleIssueError,
        },
    },
]

/**
 * Normalize Grid.
 * @param {Object} Data.data - Grid
 * @param {Object} Data.issues - issues grid by issueType
 */
const normalizeGrid = ({ data, issues }) => {
    const { selectGroups } = data
    const columns = normalizeColumns({ columns: data.columns, issues })
    const rows = normalizeRows({ rows: data.rows, columns, issues })

    return {
        ...DATAGRID_CONFIG,
        columns,
        rows,
        selectGroups,
    }
}

/**
 * Normalize columns.
 * @param {Object} columns - columns grid
 */
const normalizeColumns = ({ columns, issues }) => [
    ...(getLengthIssues(issues) ? EXTRA_COLUMNS : []),
    ...columns.map(col => ({
        ...col,
        cellClassRules: {
            'ag-cell-issue-warning': cellRuleIssueWarning,
            'ag-cell-issue-error': cellRuleIssueError,
        },
    })),
]

/**
 * Normalize rows. Add validation errors and parse issues
 * @param {Object} Data.rows - rows grid
 * @param {Object} Data.columns - columns grid
 * @param {Object} Data.issues - issues grid by issueType
 */
const normalizeRows = ({ rows, columns, issues }) => rows.map(row => normalizeRow({ row, columns, issues }))

/**
 * Normalize rows. Add validation errors and parse issues
 * @param {Object} Data.row - rows grid
 * @param {Object} Data.columns - columns grid
 * @param {Object} Data.issues - issues grid by issueType
 */
const normalizeRow = ({ row, columns, issues }) => {
    const rowId = row._inputRowId
    const rowIssues = getRowIssues(issues, rowId)
    return { ...row, ...normalizeRowIssues({ issues: rowIssues, columns }) }
}

/**
 * Normaliaze issues
 * @param {*} issues
 */
const normalizeIssues = ({ globalIssues, cellIssues, rowIssues }) => ({ global: globalIssues, cell: cellIssues, row: rowIssues })

/**
 * Get row issues
 * @param {Object} issues - issues
 * @param {Number} id - row id
 */
const getRowIssues = (issues, rowId) => Object.keys(issues).reduce((acc, type) => ({
    ...acc,
    [type]: (issues[type] || []).filter(issue => issue.row === rowId).map(issue => ({ ...issue, issueType: type })),
}), {})

/**
 * Generate issues and field errors
 * @param {Array} issues - List issues
 * @param {Array} fields - List keys of fields
 */
const normalizeRowIssues = ({ issues, columns }) => {
    const issuesCount = getLengthIssues(issues)
    if (issuesCount) {
        const cellIssuesLength = (issues[ISSUE_TYPES.CELL] || []).length
        const rowIssuesLength = (issues[ISSUE_TYPES.ROW] || []).length
        const rowIssueLevel = rowIssuesLength && issues[ISSUE_TYPES.ROW].find(({ level }) => level === ISSUE_LEVELS.ERROR) ? ISSUE_LEVELS.ERROR : ISSUE_LEVELS.WARNING

        return {
            issuesCount,
            issues,
            ...(cellIssuesLength ? generateIssueFields(issues[ISSUE_TYPES.CELL]) : {}),
            ...(rowIssuesLength ? generateIssueFields(columns, rowIssueLevel) : {}),
        }
    }

    return {
        issuesCount,
        issues: undefined,
        _issueFields: undefined,
    }
}

/**
 * Get row and cell length issues
 * @param {Object} issues - issues
 */
const getLengthIssues = ({ row, cell }) => (row || []).length + (cell || []).length

/**
 * Generate field issues.
 * Return _issueFields with fields array
 * @param {Array} issues - List issues
 */
const generateIssueFields = (issues, rowLevel) => {
    const values = Array.isArray(issues) ? issues : [issues]
    return {
        _issueFields: values.reduce((acc, { field, level }) => ({ ...acc, [field]: rowLevel || level }), {}),
    }
}

export default {
    normalizeGrid,
    normalizeRow,
    normalizeIssues,
}
